import { Heading, Box, IconButton } from '@alto-avios/alto-ui';
import { Dialog, Modal, ModalOverlay } from 'react-aria-components';
import { useRef } from 'react';
import { clsx } from 'clsx';

import { pushtoGTM } from '../../utilities';
import { Markdown } from '../Markdown';
import { Button } from '../Button';
import { HeaderProps } from '../Header';
import { useUIString } from '../services/content/hooks/useUIString';
import { usePendingOpCo } from '../../hooks/usePendingOpCo/usePendingOpCo';

import styles from './SwitcherModal.module.css';

export interface SwitcherModalProps {
  targetOpCoId?: string;
  isOpen: boolean;
  onClose: () => void;
  onPressCta: () => void;
  auth?: HeaderProps['auth'];
  size?: 'small' | 'medium' | 'large';
}

export const SwitcherModal = ({ isOpen, onClose, onPressCta, auth, targetOpCoId, size = 'small' }: SwitcherModalProps) => {
  const switcherModalHeading = useUIString('switcherModalHeading');
  const switcherModalCopy = useUIString('switcherModalCopy')
    .replace('{{OpCo.TargetName}}', targetOpCoId === 'IB' ? 'Iberia' : 'British Airways')
    .replace('{{OpCo.SourceName}}', targetOpCoId !== 'IB' ? 'Iberia' : 'British Airways');
  const switcherModalCta = useUIString('switcherModalCta').replace('{{OpCo.TargetName}}', targetOpCoId === 'IB' ? 'Iberia' : 'British Airways');
  const dialogDismissAriaLabel = useUIString('dialogDismissAriaLabel');
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const { setPendingOpco } = usePendingOpCo();

  return (
    <>
      <ModalOverlay
        className={styles.overlay}
        isOpen={isOpen}
        isDismissable
        onOpenChange={(open) => !open && onClose()}
        shouldCloseOnInteractOutside={(element) => overlayRef.current === element}
        ref={overlayRef}
      >
        <Modal className={clsx(styles.modal, styles[size])}>
          <Dialog aria-label={switcherModalHeading} className={styles.dialog} id="opco-switcher-dialog">
            <Box alignItems="center" justifyContent="spaceBetween" borderWidth="none">
              <Heading as="h2" size="xs" textAlign="start">
                {switcherModalHeading}
              </Heading>
              <div className={styles.dismiss}>
                <IconButton
                  aria-label={dialogDismissAriaLabel}
                  emphasis="quaternary"
                  iconProps={{ iconName: 'times' }}
                  onClick={() => {
                    pushtoGTM('ctaEvent', `modal - ${switcherModalHeading}`, 'cta', 'close modal');
                    onClose();
                  }}
                  styleVariant="neutral"
                  size="sm"
                />
              </div>
            </Box>
            <Box alignItems="center" display="flex" flexDirection="column" gap="lg" borderWidth="none">
              <Markdown>{switcherModalCopy}</Markdown>
              <Button
                emphasis="primary"
                size="lg"
                styleVariant="accent"
                fullWidth
                onPress={async () => {
                  pushtoGTM('ctaEvent', `modal - ${switcherModalHeading}`, 'cta', 'buttonText');
                  setPendingOpco(targetOpCoId!);
                  if (auth?.logoutProps?.href) {
                    window.location.href = auth.logoutProps.href;
                  } else {
                    onPressCta();
                  }
                }}
              >
                {switcherModalCta}
              </Button>
            </Box>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </>
  );
};
